// src/components/study/PendingReportUpdatesCard.js
//
// Study page (admins only): coordinators' weekly reports that say patients
// enrolled/randomized or screen-failed on this study. For each, the admin sees
// who reported it, for which period, ONLY those enrolled/screen-failed people
// (not the whole report), and what Enrolled and Screen
// Failures would become. "Update numbers" asks first, showing before → after;
// "Dismiss" leaves the study alone. Either way the report is stamped so it
// never comes back (rules: services/studyReportApproval.js).
// Renders nothing when there's nothing to decide.

import { useCallback, useEffect, useMemo, useState } from "react";
import { ClipboardCheck, CheckCircle2, XCircle, ArrowRight, Loader2 } from "lucide-react";
import { useTenant } from "../../context/TenantContext";
import { auth } from "../../firebase";
import { loadPendingForStudy, decideStudyReports } from "../../services/studyReportApprovalService";
import { planStudyUpdate } from "../../services/studyReportApproval";
import { segmentByKey } from "../../services/reportPeriods";
import { WrModal, BTN_PRIMARY, BTN_SOFT, BTN_SMALL, StatusChip } from "../weeklyReport/ui";

const periodLabel = (e) => segmentByKey(e.periodStart)?.label || [e.periodStart, e.periodEnd].filter(Boolean).join(" → ") || e.weekOf;
// The two statuses that change the study's numbers.
const isApprovalStatus = (p) => p.status === "enrolled" || p.status === "screenFail";
const fmtDate = (ms) => (ms ? new Date(ms).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "");

const BeforeAfter = ({ label, before, after }) => (
  <div
    className="flex items-center justify-between gap-3 py-1.5"
    role="group"
    aria-label={`${label}: ${before} to ${after}`}
  >
    <span className="text-sm text-slate-300">{label}</span>
    <span className="text-sm font-semibold text-white tabular-nums inline-flex items-center gap-1.5">
      {before}
      <ArrowRight className="w-3.5 h-3.5 text-slate-400" />
      <span className={after !== before ? "study-report-changed text-emerald-300" : ""}>{after}</span>
    </span>
  </div>
);

const PendingReportUpdatesCard = ({ study, onStudyPatched }) => {
  const { user } = useTenant();
  const by = user?.email || auth.currentUser?.email || "";
  const [state, setState] = useState({ loading: true, error: "", entries: [] });
  const [confirm, setConfirm] = useState(null); // { kind: "approve" | "dismiss", entries }
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState("");

  const load = useCallback(async () => {
    try {
      const entries = await loadPendingForStudy(study.id);
      setState({ loading: false, error: "", entries });
    } catch (err) {
      console.error("❌ Could not load report updates for this study:", err);
      setState({ loading: false, error: "Coordinator report updates couldn't be loaded.", entries: [] });
    }
  }, [study.id]);
  useEffect(() => {
    load();
  }, [load]);

  const allPlan = useMemo(() => planStudyUpdate(study, state.entries), [study, state.entries]);
  const confirmPlan = useMemo(() => (confirm ? planStudyUpdate(study, confirm.entries) : null), [confirm, study]);

  const decide = async () => {
    if (!confirm) return;
    setBusy(true);
    setActionError("");
    try {
      const after = await decideStudyReports({
        studyDocId: study.id,
        reportIds: confirm.entries.map((e) => e.reportId),
        status: confirm.kind === "approve" ? "approved" : "dismissed",
        by,
      });
      if (after) onStudyPatched?.(after);
      setConfirm(null);
      await load();
    } catch (err) {
      console.error("❌ Could not save the decision:", err);
      setActionError(err?.message || "Couldn't save. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (state.loading) return null;
  if (state.error) return <p className="mt-3 text-xs text-rose-300 px-1">{state.error}</p>;
  if (state.entries.length === 0) return null;

  return (
    <div className="study-report-card mt-3 relative rounded-2xl border border-amber-400/40 bg-amber-500/[0.08] backdrop-blur-xl p-4">
      <div className="flex flex-wrap items-start gap-3 mb-3">
        <div className="study-report-icon w-9 h-9 rounded-xl bg-amber-500/20 border border-amber-400/40 flex items-center justify-center shrink-0">
          <ClipboardCheck className="w-4 h-4 text-amber-300" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-white">
            Enrollments/randomizations and screen fails to review ({state.entries.length})
          </h3>
          <p className="text-[11px] text-slate-300">
            Approving adds to this study's numbers. If you approve all: Enrolled {allPlan.before.ENROLLED} →{" "}
            {allPlan.after.ENROLLED}, Screen Failures {allPlan.before.SCREEN_FAILURES} → {allPlan.after.SCREEN_FAILURES}.
          </p>
        </div>
        {state.entries.length > 1 && (
          <button
            type="button"
            className={BTN_PRIMARY}
            onClick={() => {
              setActionError("");
              setConfirm({ kind: "approve", entries: state.entries });
            }}
          >
            <CheckCircle2 className="w-4 h-4" />
            Approve all
          </button>
        )}
      </div>

      <ul className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2.5">
        {state.entries.map((e) => (
          <li
            key={e.reportId}
            className="weekly-report-row flex flex-col rounded-xl border border-white/10 bg-white/[0.03] p-3"
          >
            <div className="flex flex-col gap-2 flex-1">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-white">
                  {e.coordinator || "A coordinator"} reported {periodLabel(e)}
                </div>
                <div className="text-[11px] text-slate-400">
                  {e.submittedAtMs ? `Submitted ${fmtDate(e.submittedAtMs)} · ` : ""}
                  {e.enrollments ? `Enrolled/Randomized +${e.enrollments}` : ""}
                  {e.enrollments && e.screenFailures ? " · " : ""}
                  {e.screenFailures ? `Screen fail +${e.screenFailures}` : ""}
                </div>
              </div>
              {/* Only the people this approval is about: enrolled/randomized and screen fails. */}
              {e.patients.filter(isApprovalStatus).length > 0 && (
                <ul className="space-y-1">
                  {e.patients.filter(isApprovalStatus).map((p) => (
                  <li
                    key={p.key}
                    className="flex flex-wrap items-center gap-2 px-2.5 py-1.5 rounded-lg border border-white/5 bg-white/[0.02]"
                  >
                    <span className="min-w-0 flex-1 text-sm text-white truncate">
                      {p.name || "(no name)"}
                      {p.notInRegx && <span className="ml-2 text-[10px] font-medium text-amber-300">not in RegX</span>}
                    </span>
                    <span className="text-[11px] text-slate-400">MRN {p.mrn || "—"}</span>
                    <span className="text-[11px] text-slate-400">DOB {p.dob || "—"}</span>
                    {p.subjectNumber && <span className="text-[11px] text-slate-400">Subject {p.subjectNumber}</span>}
                    <StatusChip status={p.status} />
                  </li>
                ))}
                </ul>
              )}
            </div>
            <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
              <button
                type="button"
                className={BTN_SMALL}
                onClick={() => {
                  setActionError("");
                  setConfirm({ kind: "dismiss", entries: [e] });
                }}
              >
                <XCircle className="w-3 h-3" />
                Dismiss
              </button>
              <button
                type="button"
                className={`${BTN_PRIMARY} !px-3 !py-1.5 !text-xs`}
                onClick={() => {
                  setActionError("");
                  setConfirm({ kind: "approve", entries: [e] });
                }}
              >
                <CheckCircle2 className="w-3.5 h-3.5" />
                Looks good, update numbers
              </button>
            </div>
          </li>
        ))}
      </ul>

      <WrModal
        open={!!confirm}
        onClose={() => !busy && setConfirm(null)}
        icon={confirm?.kind === "dismiss" ? XCircle : CheckCircle2}
        title={confirm?.kind === "dismiss" ? "Dismiss this report?" : "Update this study's numbers?"}
        subtitle={
          confirm?.entries.length === 1
            ? `${confirm.entries[0].coordinator || "A coordinator"} · ${periodLabel(confirm.entries[0])}`
            : confirm
            ? `${confirm.entries.length} reports`
            : ""
        }
        footer={
          <>
            <button type="button" className={`${BTN_SOFT} mr-auto`} onClick={() => setConfirm(null)} disabled={busy}>
              Cancel
            </button>
            <button type="button" className={BTN_PRIMARY} onClick={decide} disabled={busy}>
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              {confirm?.kind === "dismiss" ? "Dismiss" : "Approve and update"}
            </button>
          </>
        }
      >
        {confirm?.kind === "dismiss" ? (
          <p className="text-sm text-slate-300">
            The study's Enrolled and Screen Failures stay as they are. The report itself is kept; it just won't be added
            to this study's numbers or shown here again.
          </p>
        ) : confirmPlan ? (
          <div>
            <p className="text-sm text-slate-300 mb-2">After approving, this study will show:</p>
            <div className="rounded-xl border border-white/10 bg-white/[0.03] px-3 py-1">
              <BeforeAfter label="Enrolled" before={confirmPlan.before.ENROLLED} after={confirmPlan.after.ENROLLED} />
              <BeforeAfter
                label="Screen Failures"
                before={confirmPlan.before.SCREEN_FAILURES}
                after={confirmPlan.after.SCREEN_FAILURES}
              />
            </div>
            <p className="mt-2 text-[11px] text-slate-400">
              Nothing else on the study changes. Each report can only be added once.
            </p>
          </div>
        ) : null}
        {actionError && (
          <p className="weekly-report-error mt-3 rounded-lg border border-rose-400/30 bg-rose-500/10 px-3 py-2 text-[12px] text-rose-200">
            {actionError}
          </p>
        )}
      </WrModal>
    </div>
  );
};

export default PendingReportUpdatesCard;
